import keycloak from '@/endpoints/keycloak'
import { appConfig } from '@/config/appConfig'
import { UserDto, UserRolesDto } from '@/endpoints/models'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'

/** Set from session context (`user.support`) when Keycloak role is missing or not yet loaded. */
let sessionUserSupport = false

export function setSessionUserSupport(support: boolean): void {
	sessionUserSupport = support
}

function tokenRoleNames(): string[] {
	try {
		const parsed = keycloak?.tokenParsed as
			| {
					realm_access?: { roles?: string[] }
					resource_access?: Record<string, { roles?: string[] }>
			  }
			| undefined
		if (!parsed) return []
		const realmRoles = parsed.realm_access?.roles ?? []
		const clientRoles =
			parsed.resource_access?.[appConfig.clientId]?.roles ?? []
		return [...realmRoles, ...clientRoles]
	} catch {
		return []
	}
}

function hasTokenRole(role: UserRolesDto.RolesEnum): boolean {
	return tokenRoleNames().includes(role)
}

function realmHasRole(role: UserRolesDto.RolesEnum): boolean {
	try {
		return (
			(typeof keycloak?.hasRealmRole === 'function' &&
				keycloak.hasRealmRole(role)) ||
			hasTokenRole(role)
		)
	} catch {
		return false
	}
}

/** Realm or client JWT role (Keycloak `hasRealmRole` + token fallback). */
export function hasAppRole(role: UserRolesDto.RolesEnum): boolean {
	return realmHasRole(role)
}

/** Keycloak realm: ADMIN / SUPER_ADMIN. */
export function isRealmAdmin(): boolean {
	return (
		realmHasRole(UserRolesDto.RolesEnum.ADMIN) ||
		realmHasRole(UserRolesDto.RolesEnum.SUPER_ADMIN)
	)
}

/** SUPPORT realm/client role, or `user.support` from session when role sync lags. */
export function isSupportUser(): boolean {
	return (
		realmHasRole(UserRolesDto.RolesEnum.SUPPORT) || sessionUserSupport
	)
}

/** Keycloak realm: ADMIN / SUPER_ADMIN / SUPPORT — bypass structure ACL (matches backend). */
export function isPrivilegedUser(): boolean {
	return isRealmAdmin() || isSupportUser()
}

/** Keycloak realm: READER — view-only; no edit/lock/manage regardless of structure permissions. */
export function isRealmReader(): boolean {
	return realmHasRole(UserRolesDto.RolesEnum.READER)
}

export const isLocked = (node: StructureDto) => !!node.lockUserId

export const isLockedByMe = (node: StructureDto, user: UserDto | null) =>
	!!user && user.id === node.lockUserId

export const isLockedByOther = (node: StructureDto, user: UserDto | null) =>
	isLocked(node) && !isLockedByMe(node, user)

/** True when tree/API has not projected `structurePermissions` yet (bulk payload). */
function aclNotProjected(perms: StructureDto['structurePermissions']): boolean {
	return perms === undefined
}

/**
 * Structure ACL (`structurePermissions`): `VIEW`, `EDIT`, `MANAGE_ACCESS`, `FORCE_UNLOCK`.
 * Realm `ADMIN`/`SUPER_ADMIN`/`SUPPORT` bypasses structure checks; realm `READER` has no edit/manage access.
 *
 * When ACL is missing on the node (`structurePermissions === undefined`), fall back to legacy
 * `canEdit` / allow view — bulk tree loaders often omit ACL until detail load.
 * When ACL is present (including `[]`), gates are **strict** (`includes` only).
 */
export const hasStructureEditPermission = (node: StructureDto): boolean => {
	if (isPrivilegedUser()) return true
	if (isRealmReader()) return false
	const perms = node.structurePermissions
	if (aclNotProjected(perms)) {
		return node.canEdit
	}
	return perms!.includes('EDIT')
}

export const isEditable = (node: StructureDto, user: UserDto | null) =>
	hasStructureEditPermission(node) &&
	(!node.lockUserId || isLockedByMe(node, user))

/** ACL: change who can access the structure (`MANAGE_ACCESS`). */
export const hasStructureManageAccessPermission = (node: StructureDto): boolean => {
	if (isPrivilegedUser()) return true
	if (isRealmReader()) return false
	const perms = node.structurePermissions
	if (aclNotProjected(perms)) {
		return node.canEdit
	}
	return perms!.includes('MANAGE_ACCESS')
}

/**
 * True when the user may open / view this structure: privileged realm roles bypass ACL;
 * when `structurePermissions` is set, requires `VIEW`; missing node means no.
 */
export const hasStructureViewPermission = (
	node: StructureDto | null | undefined,
): boolean => {
	if (isPrivilegedUser()) return true
	if (!node) return false
	const perms = node.structurePermissions
	if (aclNotProjected(perms)) {
		return true
	}
	return perms!.includes('VIEW')
}

/** Unlock someone else's lock (structure ACL `FORCE_UNLOCK`). */
export const hasStructureForceUnlockPermission = (
	node: StructureDto,
): boolean => {
	if (isPrivilegedUser()) return true
	if (isRealmReader()) return false
	const perms = node.structurePermissions
	if (aclNotProjected(perms)) {
		return node.canEdit
	}
	return perms!.includes('FORCE_UNLOCK')
}

/** Keycloak realm: FORCE_UNLOCK role (plus privileged users). */
export const hasRealmForceUnlockPermission = (): boolean =>
	realmHasRole(UserRolesDto.RolesEnum.FORCE_UNLOCK) || isPrivilegedUser()

/** May force-unlock a lock held by another user (structure ACL and/or realm role). */
export const canForceUnlockOthersLock = (node: StructureDto): boolean =>
	hasStructureForceUnlockPermission(node) || hasRealmForceUnlockPermission()

/** Show unlock when locked by the current user, or when force-unlock is allowed for others. */
export const shouldShowUnlockButton = (
	node: StructureDto,
	user: UserDto | null,
): boolean => {
	if (!isLocked(node)) return false
	if (isLockedByMe(node, user)) return true
	return canForceUnlockOthersLock(node)
}

/** Own lock is always unlockable; others' locks need structure and/or realm force-unlock. */
export const canUnlockStructure = (
	node: StructureDto,
	user: UserDto | null,
): boolean => shouldShowUnlockButton(node, user)
